import React, { useState, useEffect, useCallback } from "react";
import { superAdmin } from "../../api";
import { Btn, FormField } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function ManageOrgAdminsModal({ open, org, onClose, onUpdated }) {
  const [admins,   setAdmins]   = useState([]);
  const [eligible, setEligible] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    try {
      const [ar, er] = await Promise.all([
        superAdmin.getOrgAdmins(org.id),
        superAdmin.getOrgEligibleAdmins(org.id),
      ]);
      setAdmins(ar.data?.results || ar.data || []);
      setEligible(er.data?.results || er.data || []);
    } catch { toast.error("Failed to load admins."); }
    finally  { setLoading(false); }
  }, [org]);

  useEffect(() => {
    if (open) { load(); setSearch(""); setSelected(null); }
  }, [open, load]);

  const handleAssign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await superAdmin.assignOrgAdmin(org.id, { user_id: selected.id });
      toast.success(`${selected.name} is now an org admin for ${org.name}.`);
      setSelected(null);
      load();
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to assign admin.");
    } finally { setSaving(false); }
  };

  const filteredEligible = eligible.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    (u.facility_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Modal open={open} onClose={onClose} title={`Org Admins — ${org?.name || ""}`} width="560px">
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* Current admins */}
        <div>
          <div style={styles.sectionLabel}>Current Org Admins ({admins.length})</div>
          {loading
            ? <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Loading…</div>
            : admins.length === 0
              ? <div style={{ color: "#EF4444", fontSize: "13px" }}>No org admins assigned yet.</div>
              : admins.map(a => (
                <div key={a.id} style={styles.adminRow}>
                  <div style={styles.avatar}>{a.name?.[0]?.toUpperCase() || "?"}</div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{a.name}</div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{a.email}</div>
                  </div>
                </div>
              ))
          }
        </div>

        {/* Assign existing user */}
        <div style={{ borderTop: "1px solid var(--surface-border)", paddingTop: "16px" }}>
          <div style={styles.sectionLabel}>Assign Existing User as Org Admin</div>
          <div style={styles.hint}>
            Select a facility admin from the platform. Their role will be changed to org admin for this organisation.
          </div>
          <input
            placeholder="Search by name, email, or facility…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          <div style={styles.userList}>
            {filteredEligible.length === 0
              ? <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "12px 0", textAlign: "center" }}>
                  {search ? "No matching users." : "No eligible facility admins on the platform."}
                </div>
              : filteredEligible.map(u => (
                <div
                  key={u.id}
                  onClick={() => setSelected(selected?.id === u.id ? null : u)}
                  style={{
                    ...styles.userRow,
                    background: selected?.id === u.id ? "var(--surface-border)" : "var(--input-bg)",
                    borderColor: selected?.id === u.id ? "rgba(37,99,235,0.4)" : "rgba(37,99,235,0.08)",
                  }}
                >
                  <div style={styles.userAvatar}>{u.name?.[0]?.toUpperCase() || "?"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>{u.name}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      {u.email} · {u.facility_name || "No facility"}
                    </div>
                  </div>
                  {selected?.id === u.id && <span style={{ color: "#60A5FA", fontSize: "16px" }}>✓</span>}
                </div>
              ))
            }
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Close</Btn>
          <Btn onClick={handleAssign} disabled={saving || !selected}>
            {saving ? "Assigning…" : selected ? `Assign ${selected.name}` : "Select a user above"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  sectionLabel: { fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "10px" },
  hint:         { fontSize: "12px", color: "var(--text-secondary)", marginBottom: "12px" },
  adminRow:     { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "8px", marginBottom: "6px" },
  avatar:       { width: "30px", height: "30px", borderRadius: "8px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  userAvatar:   { width: "30px", height: "30px", borderRadius: "8px", background: "linear-gradient(135deg,#7C3AED,#2563EB)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  searchInput:  { background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "8px 12px", color: "var(--text-primary)", fontSize: "13px", outline: "none", width: "100%", fontFamily: "inherit", boxSizing: "border-box", marginBottom: "10px" },
  userList:     { maxHeight: "220px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" },
  userRow:      { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", border: "1px solid", borderRadius: "8px", cursor: "pointer", transition: "background 0.15s, border-color 0.15s" },
};
